import { defaultMeteringInfo } from '@common/credits/constants';
import { ExecutionPlaneCredits, MeteringInfo } from '@common/credits/types';
import { useIsGlobalTeamAdmin } from '@common/teams/hooks';
import { UserTypes } from '@common/UserTypes';
import { addUsers } from '@pages/Metadata/components/Users/api';
import { MinusIcon, PlusIcon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import {
  Button,
  Card,
  Controller,
  Dialog,
  Ellipsis,
  formatDate,
  NumberInput,
  ProgressBar,
  Separator,
  Shimmer,
  Stack,
  StackItem,
  Text,
  TextArea,
  Tooltip
} from '@prophecy/ui-v3';
import { DialogProps } from '@prophecy/ui-v3/Dialog/types';
import { StyledInputContainer } from '@prophecy/ui-v3/Input/styled';
import { Alert, toast } from '@prophecy/ui-v3/ui-compat';
import { usePersistentCallback, useSearchParamState } from '@prophecy/utils/react/hooks';
import { pluralize } from '@prophecy/utils/string';
import { useQueryClient } from '@tanstack/react-query';
import { useAppMetadata } from 'frontend/core/src/context/appMetadata';
import { useCreditsLabel } from 'frontend/core/src/context/credits';
import { useGlobalVariables } from 'frontend/core/src/context/globalConfig/securedConfigHook';
import { getCreditUsage, requestCredits } from 'frontend/core/src/data/apis/api';
import { useRestMutation, useRestQuery } from 'frontend/core/src/data/util';
import { ExecutionInfoCard } from 'frontend/core/src/generic-components/executionComponents';
import { isNil } from 'lodash-es';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';

import { SapUsageDashboard } from './SapUsage';
import { getSapMeteringInfo } from './SapUsage/api';
import { SapMeteringInfo } from './SapUsage/apiTypes';
import { LoadingBlock } from './SapUsage/styled';

const getProgressBarColor = (percent: number): string => {
  if (percent >= 99) return theme.colors.error600;
  if (percent >= 80) return theme.colors.warning600;
  return theme.colors.brand600;
};

const getProgressBarVariant = (percent: number): 'success' | 'error' | 'loading' => {
  if (percent >= 80) return 'error';
  return 'success';
};

// Custom styled Card with specific padding and border styles
const StyledCard = styled(Card)<{ $error?: boolean }>`
  padding: ${theme.spaces.x12} ${theme.spaces.x16};
  border-radius: ${theme.radius.x8};
  border: 1px solid ${({ $error }) => ($error ? theme.colors.error200 : theme.colors.gray200)};
  background: ${theme.colors.white};
  box-shadow: none;

  &:hover {
    box-shadow: none;
  }
`;

// Custom styled ProgressBar with specific height and colors
const StyledProgressBar = styled(ProgressBar)`
  height: ${theme.sizes.x20};
  border-radius: ${theme.radius.x4};

  > div {
    height: ${theme.sizes.x20};
    border-radius: ${theme.radius.x4};

    > div {
      border-radius: ${theme.radius.x4};
    }
  }
`;

// Styled input and container for seat count
const SeatCountContainer = styled.div`
  min-width: 160px;
  min-height: 54px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid ${theme.colors.brand500};
  border-radius: ${theme.radius.x12};
  background: ${theme.colors.white};
  cursor: text;
`;

const SeatCountInput = styled(NumberInput)<{ $width?: number }>`
  text-align: center;
  background: transparent;
  width: ${({ $width }) => ($width ? `${$width}px` : '20px')};
  min-width: 20px;

  ${StyledInputContainer} {
    padding: 0;
    outline: none;
    box-shadow: none;
    font-size: ${theme.fontSizes.x36};
    font-weight: ${theme.fontWeight.semiBold};

    &:focus,
    &:active {
      outline: none;
      box-shadow: none;
    }

    input {
      color: ${theme.colors.brand600};
    }
  }
`;

const SeatCountMeasure = styled.span`
  position: absolute;
  visibility: hidden;
  height: 0;
  overflow: hidden;
  font-size: ${theme.fontSizes.x36};
  font-weight: ${theme.fontWeight.semiBold};
  font-family: inherit;
  white-space: pre;
`;

const FullWidthButton = styled(Button)`
  width: 100%;
  margin-top: ${theme.spaces.x8};
` as unknown as typeof Button;
/* // Add a helper for currency formatting
const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value); */

// Add a helper function to format numbers: show up to 2 decimal places for floats, no decimals for integers. Use this helper for all numbers in the credit info sections (credit usage, breakdown, execution planes, users).
function formatDisplayNumber(value: number): string {
  if (Number.isInteger(value)) return value.toLocaleString();
  return value.toLocaleString(undefined, { maximumFractionDigits: 3, minimumFractionDigits: 0 });
}

function CountInput({
  value,
  onChange,
  min = 1,
  max = 999999,
  allowDecimal = false,
  allowNegative = false
}: {
  value: number;
  onChange: (value?: number) => void;
  min?: number;
  max?: number;
  allowDecimal?: boolean;
  allowNegative?: boolean;
}) {
  const [inputWidth, setInputWidth] = useState(20);
  const measureRef = useRef<HTMLSpanElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useLayoutEffect(() => {
    if (measureRef.current) {
      setInputWidth(Math.max(20, Math.min(140, measureRef.current.offsetWidth)));
    }
  }, [value]);

  const handleContainerClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (inputRef.current && e.target !== inputRef.current) {
      inputRef.current.focus();
      requestAnimationFrame(() => {
        if (!inputRef.current) return;
        const type = inputRef.current.type;
        inputRef.current.type = 'text';
        inputRef.current.setSelectionRange(-1, -1);
        inputRef.current.type = type;
      });
    }
  };

  const handleDecrement = () => onChange(Math.max(min, value - 1));
  const handleIncrement = () => onChange(Math.min(max, value + 1));

  return (
    <Stack direction='horizontal' alignY='center' gap={theme.spaces.x12} align='center'>
      <Button
        variant='secondaryGray'
        size='xxl'
        onClick={handleDecrement}
        tabIndex={-1}
        icon={<MinusIcon type='default' />}
        aria-label='Decrease count'
        data-test-id='usage-decrement-button'
      />
      <SeatCountContainer onClick={handleContainerClick}>
        <SeatCountInput
          ref={inputRef}
          min={min}
          max={max}
          allowDecimal={allowDecimal}
          allowNegative={allowNegative}
          value={value}
          onChange={onChange}
          inputMode='numeric'
          noBorder
          noBoxShadow
          $width={inputWidth}
          data-test-id='usage-seat-count-input'
        />
        <SeatCountMeasure ref={measureRef}>{value}</SeatCountMeasure>
      </SeatCountContainer>
      <Button
        variant='secondaryGray'
        size='xxl'
        onClick={handleIncrement}
        tabIndex={-1}
        icon={<PlusIcon type='default' />}
        aria-label='Increase seat count'
        data-test-id='usage-add-seat-button'
      />
    </Stack>
  );
}
// Add styled components for execution plane and user cards
const StyledExecutionInfoCard = styled(ExecutionInfoCard)`
  width: 220px;
`;

const UserCard = styled(StyledCard)`
  min-width: 140px;
`;

const AddSeatsButton = styled(Button)`
  height: auto;

  &,
  &:active {
    border-width: 0 !important;
  }
` as unknown as typeof Button;
// --- Helper Components ---
function CreditConsumptionPopover({ info }: { info: MeteringInfo }) {
  const creditLabelWithOEM = useCreditsLabel();

  return (
    <Text level='xs'>
      Consumption of {creditLabelWithOEM}s:
      <br />
      {info.creditsCostPerGBProcessed} / GB Processed
      <br />
      {info.creditsCostPerGBEgressed} / GB Egressed
      <br />
      {info.creditsCostPerGBStoredPerMonth} / GB Stored / Month
      <br />
      {info.creditsCostPerAdditionalUser} / Additional User / Year
    </Text>
  );
}

function CreditUsageBreakdown({ info, usedPercent }: { info: MeteringInfo; usedPercent: number }) {
  return (
    <Stack gap={theme.spaces.x12}>
      <StyledProgressBar
        value={usedPercent}
        width='100%'
        color={getProgressBarColor(usedPercent)}
        variant={getProgressBarVariant(usedPercent)}
      />
      <Stack direction='horizontal' alignY='center'>
        <Tooltip title={<CreditConsumptionPopover info={info} />} placement='right' align='start'>
          <Stack direction='horizontal' gap={theme.spaces.x8} alignY='center'>
            <Text level='xs' tone={theme.textColor.secondary}>
              {formatDisplayNumber(info.creditsConsumed)} ({usedPercent}%)
            </Text>
            <Text level='xs' tone={theme.colors.gray400}>
              |
            </Text>
            <Text level='xs' tone={theme.textColor.secondary}>
              {formatDisplayNumber(info.creditsBreakdown.dataProcessedCredits)} in Data Processed
            </Text>
            <Text level='xs' tone={theme.colors.gray400}>
              |
            </Text>
            <Text level='xs' tone={theme.textColor.secondary}>
              {formatDisplayNumber(info.creditsBreakdown.dataEgressedCredits)} in Data Egressed
            </Text>
            <Text level='xs' tone={theme.colors.gray400}>
              |
            </Text>
            <Text level='xs' tone={theme.textColor.secondary}>
              {formatDisplayNumber(info.creditsBreakdown.dataStoredCredits)} in Data Stored
            </Text>
            <Text level='xs' tone={theme.colors.gray400}>
              |
            </Text>
            <Text level='xs' tone={theme.textColor.secondary}>
              {formatDisplayNumber(info.creditsBreakdown.additionalUsersCredits)} in Additional Users
            </Text>
          </Stack>
        </Tooltip>
        <StackItem grow='1' />
        <Text level='xs' tone={theme.textColor.secondary}>
          {formatDisplayNumber(info.totalCreditsAllocated)}
        </Text>
      </Stack>
    </Stack>
  );
}

function ExecutionPlanesSection({ planes }: { planes: ExecutionPlaneCredits[] }) {
  const planesInfo = useMemo(() => {
    return planes.map((plane) => ({
      key: plane.executionPlaneID,
      items: [
        { label: 'Data Processed', value: plane.creditsBreakdown.dataProcessedCredits },
        { label: 'Data Egressed', value: plane.creditsBreakdown.dataEgressedCredits },
        { label: 'Data Stored', value: plane.creditsBreakdown.dataStoredCredits }
      ],
      title: plane.controlPlaneURL
    }));
  }, [planes]);

  return (
    <Stack direction='horizontal' gap={theme.spaces.x16} wrap='wrap'>
      {planesInfo.map((info) => (
        <StyledExecutionInfoCard key={info.key} items={info.items} title={info.title} />
      ))}
    </Stack>
  );
}

function UsersSection({ info, onAddUsers }: { info: MeteringInfo; onAddUsers: () => void }) {
  const userCards = [
    { label: 'Included Users', value: info.includedUsers },
    {
      label: 'Additional Users',
      value: (
        <Stack direction='horizontal' gap={theme.spaces.x4} alignY='center'>
          <StackItem grow='1'>
            <Ellipsis tooltip>
              <Text level='xs' weight={theme.fontWeight.semiBold}>
                {formatDisplayNumber(info.additionalUsers)}
              </Text>
            </Ellipsis>
          </StackItem>
          <AddSeatsButton size='xs' variant='link' onClick={onAddUsers} data-test-id='usage-add-users-button'>
            Add users
          </AddSeatsButton>
        </Stack>
      )
    },
    { label: 'Total Users', value: info.totalUsers },
    { label: 'Activated Users', value: info.activatedUsers }
  ];
  return (
    <Stack gap={theme.spaces.x16}>
      <Text level='md' weight={theme.fontWeight.medium}>
        Users
      </Text>
      <Stack direction='horizontal' gap={theme.spaces.x16}>
        {userCards.map((card) => (
          <UserCard key={card.label}>
            <Stack gap={theme.spaces.x8} align='center'>
              <Text level='xs' tone={theme.textColor.tertiary} weight={theme.fontWeight.medium}>
                {card.label}
              </Text>
              {typeof card.value === 'number' ? (
                <Text level='xs' weight={theme.fontWeight.medium}>
                  {formatDisplayNumber(card.value)}
                </Text>
              ) : (
                card.value
              )}
            </Stack>
          </UserCard>
        ))}
      </Stack>
    </Stack>
  );
}

// --- Helper: SAP Success Dialog ---
function showSAPSuccessDialog(email?: string) {
  Dialog.success({
    size: 'm',
    title: 'Your request has been sent to SAP for review',
    subTitle: (
      <>
        We've sent a confirmation email to <b>{email || 'your email'}</b>.
      </>
    ),
    okButton: { children: 'Done', variant: 'primary', style: { width: '100%' } },
    'data-test-id': 'sap-success-dialog'
  });
}

const SAP_METERING_QUERY_KEY = ['sap-metering-info'];

// --- Main Usage Component ---
export const Usage = () => {
  const { data } = useRestQuery<MeteringInfo>(['get-credit-usage'], () => getCreditUsage(), {
    retry: false,
    refetchOnWindowFocus: false
  });
  const meteringInfo: MeteringInfo = (data?.data as MeteringInfo) || defaultMeteringInfo;
  const [isAddSeatsOpen, setAddSeatsOpen] = useState(false);
  const [isRequestCreditsOpen, setRequestCreditsOpen] = useSearchParamState('request-credits', {
    initialValue: false,
    deserialize: (value) => value === 'true'
  });
  const usedPercent = Math.round(
    (meteringInfo.creditsConsumed / (meteringInfo.totalCreditsAllocated || meteringInfo.creditsConsumed || 1)) * 100
  );

  const updatedAtDate = new Date(meteringInfo.updated_at);
  const fromDate = new Date(meteringInfo.from);
  const toDate = new Date(meteringInfo.to);
  const remainingDays = meteringInfo.to
    ? Math.round((toDate.getTime() - new Date().getTime()) / (24 * 60 * 60 * 1000))
    : undefined;

  const creditLabelWithOEM = useCreditsLabel();
  const { isSapEnabled } = useGlobalVariables();
  const { isGlobalTeamAdmin, isLoading: isTeamsLoading } = useIsGlobalTeamAdmin({ enabled: isSapEnabled });
  const userType = useAppMetadata()?.user?.type;
  // Two separate rules, not two definitions of one: SAP scopes admin to the global team, while the
  // legacy credits view keeps the cluster-admin/support check it shipped with.
  const isAdmin = isSapEnabled
    ? isGlobalTeamAdmin
    : userType === UserTypes.ClusterAdmin || userType === UserTypes.Support;

  const sapMetering = useRestQuery<SapMeteringInfo>(SAP_METERING_QUERY_KEY, () => getSapMeteringInfo(), {
    retry: false,
    refetchOnWindowFocus: false,
    enabled: isSapEnabled
  });

  // The shared endpoint now returns the new meteringInfo shape, which carries the per-user
  // rate under rates.infrastructure rather than as a flat field.
  const sapRates = sapMetering.data?.data?.rates?.infrastructure;
  const dialogInfo: MeteringInfo =
    isSapEnabled && sapRates
      ? { ...meteringInfo, creditsCostPerAdditionalUser: sapRates.perAdditionalUser }
      : meteringInfo;

  const dialogs = isAdmin && (
    <>
      {isAddSeatsOpen && <AddSeatsDialog onClose={() => setAddSeatsOpen(false)} info={dialogInfo} />}
      {isRequestCreditsOpen && (
        <RequestMoreCreditsDialog onClose={() => setRequestCreditsOpen(false)} info={dialogInfo} />
      )}
    </>
  );

  if (isSapEnabled) {
    if (sapMetering.isLoading || isTeamsLoading) {
      return (
        <Shimmer loading fullWidth data-test-id='sap-usage-loading'>
          <Stack gap={theme.spaces.x24}>
            <LoadingBlock />
            <LoadingBlock />
            <LoadingBlock />
          </Stack>
        </Shimmer>
      );
    }
    if (sapMetering.isError || !sapMetering.data?.data) {
      return (
        <Alert variant='error' data-test-id='sap-usage-error-alert'>
          Could not load your {creditLabelWithOEM} usage. Refresh to try again.
        </Alert>
      );
    }

    return (
      <>
        {dialogs}
        <SapUsageDashboard
          meteringInfo={sapMetering.data.data}
          isAdmin={isAdmin}
          onRequestCapacity={isAdmin ? () => setRequestCreditsOpen(true) : undefined}
          onAddUsers={() => setAddSeatsOpen(true)}
        />
      </>
    );
  }

  return (
    <Stack align='space-between' height='100%'>
      {/* Dialogs */}
      {dialogs}
      {/* Main Card Section */}
      <Stack gap={theme.spaces.x48}>
        <Stack gap={theme.spaces.x24}>
          <Text level='md' weight={theme.fontWeight.medium}>
            {creditLabelWithOEM}s
          </Text>
          <StyledCard $error={usedPercent >= 99}>
            <Stack gap={theme.spaces.x16}>
              <Stack direction='horizontal' alignY='center'>
                <Stack gap={theme.spaces.x6}>
                  <Text level='sm'>Usage of {creditLabelWithOEM}s</Text>
                  <Text level='xs' tone={theme.colors.gray600} weight={theme.fontWeight.normal}>
                    Last updated:{' '}
                    {!meteringInfo.updated_at
                      ? '--'
                      : `${formatDate(updatedAtDate, `dd MMM yyy`)} at ${formatDate(updatedAtDate, `hh:mm aa`)}`}
                  </Text>
                </Stack>
                <StackItem grow='1' />
                <Button
                  size='s'
                  variant='secondary'
                  onClick={() => setRequestCreditsOpen(true)}
                  data-test-id='usage-request-more-credits-button'>
                  Request More {creditLabelWithOEM}s
                </Button>
              </Stack>
              <CreditUsageBreakdown usedPercent={usedPercent} info={meteringInfo} />
            </Stack>
          </StyledCard>
          <ExecutionPlanesSection planes={meteringInfo.executionPlanesCredits || []} />
        </Stack>
        <UsersSection info={meteringInfo} onAddUsers={() => setAddSeatsOpen(true)} />
      </Stack>
      <Stack gap={theme.spaces.x12}>
        <Separator />
        <Stack direction='horizontal' align={'space-between'} gap={theme.spaces.x16}>
          <StackItem grow='0' shrink='0'>
            <Text level='sm' tone={theme.textColor.secondary}>
              Contract Duration
            </Text>
          </StackItem>
          <Stack direction='horizontal' gap={theme.spaces.x8} alignY='center'>
            <Text level='xs' tone={theme.textColor.tertiary}>
              Start: {meteringInfo.from ? formatDate(fromDate, `dd MMM yyy`) : '--'}
            </Text>
            <Text level='xs' tone={theme.colors.gray400}>
              |
            </Text>
            <Text level='xs' tone={theme.textColor.tertiary}>
              End: {meteringInfo.to ? formatDate(toDate, `dd MMM yyy`) : '--'}
            </Text>
            {!isNil(remainingDays) && (
              <>
                <Text level='xs' tone={theme.colors.gray400}>
                  |
                </Text>
                <Text level='xs' tone={theme.textColor.tertiary}>
                  {remainingDays} {pluralize(remainingDays, 'Day')} remaining
                </Text>
              </>
            )}
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};

// AddSeatsDialog component
function AddSeatsDialog({ onClose, info }: Omit<DialogProps, 'data-test-id'> & { info: MeteringInfo }) {
  const queryClient = useQueryClient();
  const creditLabelWithOEM = useCreditsLabel();

  const formContext = useForm({
    mode: 'onChange',
    defaultValues: {
      userCount: 2
    }
  });

  const { control, watch } = formContext;
  const userCount = watch('userCount');
  const costPerUser = Number.isFinite(info.creditsCostPerAdditionalUser)
    ? info.creditsCostPerAdditionalUser
    : undefined;

  const { mutateAsync: addUsersMutation, isLoading: isAddingUsers } = useRestMutation(addUsers, {
    sentryTag: 'AddSeatsDialog-addUsers'
  });

  const handleRequest = async () => {
    formContext.handleSubmit(async ({ userCount }) => {
      const resp = await addUsersMutation({ userCount });
      queryClient.invalidateQueries(['get-credit-usage']);
      queryClient.invalidateQueries(SAP_METERING_QUERY_KEY);
      onClose?.();

      const email = (resp as { data?: { partnerEmail?: string } })?.data?.partnerEmail;
      showSAPSuccessDialog(email);
    })();
  };

  return (
    <Dialog
      open
      onClose={onClose}
      size='m'
      showCloseIcon
      closeButton={null}
      okButton={null}
      data-test-id='usage-add-users-dialog'>
      <Stack gap={theme.spaces.x24}>
        <Text level='lg' weight={theme.fontWeight.medium} tone={theme.colors.gray900}>
          Add Users
        </Text>
        <Stack gap={theme.spaces.x12}>
          <Stack direction='horizontal' alignY='center' gap={theme.spaces.x16}>
            <Text level='sm' tone={theme.textColor.tertiary}>
              Included Users
            </Text>
            <StackItem grow='1' />
            <Text level='sm'>{info.includedUsers}</Text>
          </Stack>
          <Stack direction='horizontal' alignY='center' gap={theme.spaces.x16}>
            <Text level='sm' tone={theme.textColor.tertiary}>
              Additional Users
            </Text>
            <StackItem grow='1' />
            <Text level='sm'>{info.additionalUsers}</Text>
          </Stack>
          <Stack direction='horizontal' alignY='center' gap={theme.spaces.x16}>
            <Text level='sm' tone={theme.textColor.tertiary}>
              Total Users
            </Text>
            <StackItem grow='1' />
            <Text level='sm'>{info.totalUsers}</Text>
          </Stack>
          <Stack direction='horizontal' alignY='center' gap={theme.spaces.x16}>
            <Text level='sm' tone={theme.textColor.tertiary}>
              Activated Users
            </Text>
            <StackItem grow='1' />
            <Text level='sm'>{info.activatedUsers}</Text>
          </Stack>
          <Stack direction='horizontal' alignY='center' gap={theme.spaces.x16}>
            <Text level='sm' tone={theme.textColor.tertiary}>
              {creditLabelWithOEM}s/Additional User
            </Text>
            <StackItem grow='1' />
            <Text level='sm'>{costPerUser ?? '—'}</Text>
          </Stack>
        </Stack>
        <Controller
          render={({ field }) => (
            <CountInput
              {...field}
              onChange={(value) => field.onChange(value || 1)}
              data-test-id='add-seats-user-count-input'
            />
          )}
          control={control}
          name='userCount'
        />
        {costPerUser !== undefined && (
          <Alert variant='info' data-test-id='add-seats-cost-alert'>
            This will cost {userCount * costPerUser} additional credits
          </Alert>
        )}
        <FullWidthButton
          variant='primary'
          size='l'
          onClick={handleRequest}
          loading={isAddingUsers}
          data-test-id='add-seats-submit-button'>
          Add Users
        </FullWidthButton>
      </Stack>
    </Dialog>
  );
}

// RequestMoreCreditsDialog component
function RequestMoreCreditsDialog({
  onClose,
  info
}: Omit<DialogProps, 'data-test-id'> & {
  info: MeteringInfo;
}) {
  const queryClient = useQueryClient();
  const creditLabelWithOEM = useCreditsLabel();
  const [creditCount, setCreditCount] = useState<number>(300);
  const [notes, setNotes] = useState('');
  const updateCreditCount = usePersistentCallback((value = 1) => setCreditCount(value));
  const { mutateAsync, isLoading } = useRestMutation(requestCredits, {
    sentryTag: 'RequestMoreCreditsDialog-requestCredits'
  });

  const handleRequest = async () => {
    try {
      const resp = await mutateAsync({ creditsRequested: creditCount, notes });
      onClose?.();
      queryClient.invalidateQueries(['get-credit-usage']);
      queryClient.invalidateQueries(SAP_METERING_QUERY_KEY);
      const email = (resp as { data?: { partnerEmail?: string } })?.data?.partnerEmail;
      showSAPSuccessDialog(email);
    } catch (error) {
      toast.error({ content: `Failed to add ${creditLabelWithOEM}s` });
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      size='m'
      showCloseIcon
      closeButton={null}
      okButton={null}
      data-test-id='usage-request-credits-dialog'>
      <Stack gap={theme.spaces.x24}>
        <Text level='lg' weight={theme.fontWeight.medium} tone={theme.colors.gray900}>
          Request More {creditLabelWithOEM}s
        </Text>
        <Stack gap={theme.spaces.x12}>
          <Stack direction='horizontal' alignY='center' gap={theme.spaces.x16}>
            <Text level='sm' tone={theme.textColor.tertiary}>
              {creditLabelWithOEM}s/GB Stored/Month
            </Text>
            <StackItem grow='1' />
            <Text level='sm'>{info.creditsCostPerGBStoredPerMonth}</Text>
          </Stack>
          <Stack direction='horizontal' alignY='center' gap={theme.spaces.x16}>
            <Text level='sm' tone={theme.textColor.tertiary}>
              {creditLabelWithOEM}s/GB Processed
            </Text>
            <StackItem grow='1' />
            <Text level='sm'>{info.creditsCostPerGBProcessed}</Text>
          </Stack>
        </Stack>
        <CountInput value={creditCount} onChange={updateCreditCount} />
        <TextArea label={'Notes (Optional)'} value={notes} onChange={setNotes} data-test-id='usage-notes-textarea' />
        <FullWidthButton
          variant='primary'
          size='l'
          onClick={handleRequest}
          loading={isLoading}
          data-test-id='usage-request-more-credits-button'>
          Request More {creditLabelWithOEM}s
        </FullWidthButton>
      </Stack>
    </Dialog>
  );
}

export default Usage;
